// 📁 src/pages/LiveStreamHost.js

import React, { useRef, useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../utils/firebaseconfig";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  arrayUnion,
  query,
  orderBy
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "../styles/LiveStreamHost.css";
import { checkVerification } from "../utils/verificationCheck";

// WebRTC configuration with STUN and TURN servers
const iceServers = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    { urls: "stun:stun3.l.google.com:19302" },
    { urls: "stun:stun4.l.google.com:19302" },
    {
      urls: "turn:openrelay.metered.ca:80",
      username: "openrelayproject",
      credential: "openrelayproject"
    },
    {
      urls: "turn:openrelay.metered.ca:443",
      username: "openrelayproject",
      credential: "openrelayproject"
    },
    {
      urls: "turn:openrelay.metered.ca:443?transport=tcp",
      username: "openrelayproject",
      credential: "openrelayproject"
    }
  ],
  iceCandidatePoolSize: 10,
};

function LiveStreamHost() {
  const navigate = useNavigate();
  const localVideoRef = useRef(null);
  const peerConnections = useRef({});
  const fileInputRef = useRef(null);

  const [user, setUser] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamId, setStreamId] = useState(null);
  const [title, setTitle] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [duration, setDuration] = useState(5);
  const [endTime, setEndTime] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [timeLeft, setTimeLeft] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [devicesAvailable, setDevicesAvailable] = useState({
    camera: false,
    microphone: false,
    checking: true
  });

  // Check for available devices when component mounts
  useEffect(() => {
    const checkDevices = async () => {
      try {
        setDevicesAvailable(prev => ({ ...prev, checking: true }));
        
        // Get list of connected devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        
        // Check if any video input devices exist
        const hasCamera = devices.some(device => device.kind === 'videoinput');
        // Check if any audio input devices exist
        const hasMicrophone = devices.some(device => device.kind === 'audioinput');
        
        console.log("Available devices:", {
          camera: hasCamera ? "Found" : "Not found",
          microphone: hasMicrophone ? "Found" : "Not found",
          devices: devices.map(d => `${d.kind}: ${d.label || 'unnamed'}`)
        });
        
        setDevicesAvailable({
          camera: hasCamera,
          microphone: hasMicrophone,
          checking: false
        });
      } catch (error) {
        console.error("Error checking devices:", error);
        setDevicesAvailable({
          camera: false,
          microphone: false,
          checking: false,
          error: error.message
        });
      }
    };
    
    checkDevices();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (userData) => {
      if (!userData) {
        alert("Please log in to start a livestream.");
        navigate("/login");
      } else {
        setUser(userData);
        setIsVerified(userData.emailVerified);
        
        // If not verified, show verification warning
        if (!userData.emailVerified) {
          alert("Email verification is required to host livestreams. Please verify your email.");
        }
      }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!endTime) return;
    const interval = setInterval(() => {
      const now = new Date();
      const diff = new Date(endTime) - now;
      if (diff <= 0) {
        setTimeLeft("⏰ Bidding ended");
        clearInterval(interval);
      } else {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${minutes}m ${seconds}s`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert("Image size should be less than 5MB");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Test camera and microphone permission
  const testMediaDevices = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      // Display preview
      localVideoRef.current.srcObject = stream;
      // Clean up after 5 seconds
      setTimeout(() => {
        if (!isStreaming) {
          stream.getTracks().forEach(track => track.stop());
          localVideoRef.current.srcObject = null;
        }
      }, 5000);
      return true;
    } catch (error) {
      console.error("Media test failed:", error);
      alert(`Camera/microphone access failed: ${error.message}. Please check device permissions.`);
      return false;
    }
  };

  const handleStartStream = async (e) => {
    e.preventDefault();
    
    // Check if user is verified
    if (!checkVerification(navigate)) {
      return;
    }
    
    if (!title.trim()) return alert("Please enter a title.");

    // Check for camera and microphone availability
    if (!devicesAvailable.camera || !devicesAvailable.microphone) {
      const missingDevices = [];
      if (!devicesAvailable.camera) missingDevices.push("camera");
      if (!devicesAvailable.microphone) missingDevices.push("microphone");
      
      alert(`Cannot start stream: Missing ${missingDevices.join(" and ")}. Please connect the required devices and refresh the page.`);
      return;
    }

    try {
      setIsUploading(true);
      let imageUrl = "";

      if (imageFile) {
        const imageRef = ref(storage, `livestream-covers/${Date.now()}-${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      const docRef = await addDoc(collection(db, "livestreams"), {
        title,
        host: user.displayName || "Host",
        ownerID: user.uid,
        image: imageUrl || user.photoURL || "https://placehold.co/400x200?text=Live",
        isLive: true,
        createdAt: serverTimestamp(),
        endTime: new Date(Date.now() + duration * 60000).toISOString()
      });

      setStreamId(docRef.id);
      setIsStreaming(true);
      setEndTime(new Date(Date.now() + duration * 60000));

      // Try to get stream with ideal constraints first
      let stream;
      try {
        console.log("Attempting to get media with ideal constraints");
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          }, 
          audio: true 
        });
      } catch (mediaError) {
        console.warn("Failed with ideal constraints, trying simpler constraints:", mediaError);
        try {
          // Fallback to very basic constraints
          stream = await navigator.mediaDevices.getUserMedia({ 
            video: true, 
            audio: true 
          });
        } catch (fallbackError) {
          // Handle permission denied or hardware not available
          throw new Error(`Camera/microphone access failed: ${fallbackError.message}`);
        }
      }

      // Check if we actually got video tracks
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length === 0) {
        throw new Error("No video camera available");
      }
      
      console.log("Video stream acquired successfully. Video tracks:", videoTracks.length);
      console.log("Video track settings:", videoTracks[0].getSettings());
      
      localVideoRef.current.srcObject = stream;

      const viewersRef = collection(db, "livestreams", docRef.id, "viewers");

      onSnapshot(viewersRef, (snapshot) => {
        let count = 0;
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === "added") {
            const viewerId = change.doc.id;
            const viewerRef = doc(viewersRef, viewerId);
            if (peerConnections.current[viewerId]) return;

            console.log(`Setting up connection for viewer: ${viewerId}`);
            const pc = new RTCPeerConnection(iceServers);

            // Debug connection state changes
            pc.onconnectionstatechange = () => {
              console.log(`Connection state for ${viewerId}: ${pc.connectionState}`);
            };

            // Debug ICE connection state changes
            pc.oniceconnectionstatechange = () => {
              console.log(`ICE connection state for ${viewerId}: ${pc.iceConnectionState}`);
            };

            // Debug ICE gathering state changes
            pc.onicegatheringstatechange = () => {
              console.log(`ICE gathering state for ${viewerId}: ${pc.iceGatheringState}`);
            };

            peerConnections.current[viewerId] = pc;
            
            stream.getTracks().forEach((track) => {
              console.log(`Adding track to connection: ${track.kind}`);
              pc.addTrack(track, stream);
            });

            console.log("Creating offer for viewer");
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            console.log("Local description set, updating firestore with offer");

            await updateDoc(viewerRef, {
              offer: { type: offer.type, sdp: offer.sdp },
              iceCandidates: [],
            });

            pc.onicecandidate = (event) => {
              if (event.candidate) {
                console.log(`Generated ICE candidate for ${viewerId}`);
                updateDoc(viewerRef, {
                  iceCandidates: arrayUnion(event.candidate.toJSON()),
                });
              }
            };

            onSnapshot(viewerRef, (snap) => {
              const data = snap.data();
              const thisPc = peerConnections.current[viewerId];
              if (!thisPc || thisPc.signalingState === "closed") return;

              if (data?.answer && !thisPc.currentRemoteDescription) {
                console.log(`Received answer from viewer ${viewerId}, setting remote description`);
                thisPc.setRemoteDescription(new RTCSessionDescription(data.answer))
                  .then(() => console.log(`Remote description set for ${viewerId}`))
                  .catch(error => console.error(`Error setting remote description for ${viewerId}:`, error));
              }

              if (data?.iceCandidates) {
                console.log(`Processing ${data.iceCandidates.length} ICE candidates from viewer ${viewerId}`);
                data.iceCandidates.forEach((candidate) => {
                  thisPc.addIceCandidate(new RTCIceCandidate(candidate))
                    .catch(error => console.warn(`Failed to add ICE candidate for ${viewerId}:`, error));
                });
              }
            });
          }
        });

        snapshot.forEach((doc) => {
          if (doc.data()?.answer) count++;
        });
        setViewerCount(count);
      });

      const chatRef = collection(db, "livestreams", docRef.id, "chat");
      const chatQuery = query(chatRef, orderBy("timestamp"));
      onSnapshot(chatQuery, (snap) => {
        setMessages(snap.docs.map(doc => doc.data()));
      });

      // Update database with owner ID
      await updateDoc(doc(db, "livestreams", docRef.id), {
        ownerID: user.uid
      });

      setIsUploading(false);
    } catch (error) {
      setIsUploading(false);
      console.error("Error starting stream:", error);
      alert("Error starting stream: " + error.message);
      
      // Clean up if there was an error
      if (streamId) {
        try {
          await updateDoc(doc(db, "livestreams", streamId), { 
            isLive: false,
            error: error.message
          });
        } catch (cleanupError) {
          console.error("Error during cleanup:", cleanupError);
        }
      }
      setIsStreaming(false);
    }
  };

  const stopCamera = async () => {
    const stream = localVideoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      localVideoRef.current.srcObject = null;
    }
    Object.values(peerConnections.current).forEach((pc) => pc.close());
    peerConnections.current = {};
    setIsStreaming(false);
    if (streamId) {
      await updateDoc(doc(db, "livestreams", streamId), { 
        isLive: false,
        endedAt: serverTimestamp()
      });
    }
  };

  const handleNavigateToAuctions = () => {
    navigate("/live-auction");
  };

  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (streamId) {
        try {
          await updateDoc(doc(db, "livestreams", streamId), { isLive: false });
        } catch (error) {
          console.warn("Failed to mark stream as offline:", error);
        }
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      handleBeforeUnload();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [streamId]);

  return (
    <div className="live-stream-host">
      <h2>🔴 Host Livestream</h2>
      
      {!isVerified && (
        <div className="verification-warning">
          <p><strong>Email verification required!</strong> You need to verify your email before hosting livestreams.</p>
          <button 
            onClick={() => checkVerification(navigate)}
            className="verify-email-button"
          >
            Verify Email
          </button>
        </div>
      )}
      
      {/* Device Status Warning */}
      {!devicesAvailable.checking && (!devicesAvailable.camera || !devicesAvailable.microphone) && (
        <div className="device-warning">
          <p><strong>⚠️ Missing required devices:</strong></p>
          <ul>
            {!devicesAvailable.camera && <li>Camera not found or permission denied</li>}
            {!devicesAvailable.microphone && <li>Microphone not found or permission denied</li>}
          </ul>
          <p>Please check your device connections and browser permissions.</p>
          <button onClick={() => window.location.reload()} className="refresh-button">
            Refresh Page
          </button>
        </div>
      )}
      
      {!isStreaming ? (
        <form className="live-stream-form" onSubmit={handleStartStream}>
          <input 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="Enter your livestream title"
            required 
            disabled={!isVerified}
          />
          
          <div className="image-upload">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              disabled={!isVerified}
            />
            <button 
              type="button" 
              className="upload-button"
              onClick={() => isVerified && fileInputRef.current.click()}
              disabled={!isVerified}
            >
              📸 Upload Cover Image
            </button>
            {imagePreview ? (
              <div className="image-preview">
                <img src={imagePreview} alt="Cover preview" />
                <button 
                  type="button"
                  className="remove-image"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview("");
                  }}
                  disabled={!isVerified}
                >
                  ❌
                </button>
              </div>
            ) : (
              <p style={{ color: '#666', marginTop: '10px', fontSize: '14px' }}>
                Upload an image to display as your stream cover
              </p>
            )}
          </div>

          <div className="duration-field">
            <label htmlFor="duration-input">Bidding Duration (minutes):</label>
            <input
              id="duration-input"
              type="number"
              value={duration}
              min="1"
              onChange={(e) => setDuration(Number(e.target.value))}
              placeholder="Bidding Duration (minutes)"
              required
              disabled={!isVerified}
            />
            <span className="duration-indicator">{duration} {duration === 1 ? 'minute' : 'minutes'}</span>
          </div>
          
          {/* Camera test button */}
          <button 
            type="button" 
            className="test-camera-button"
            onClick={testMediaDevices}
            disabled={!isVerified || !devicesAvailable.camera || !devicesAvailable.microphone}
          >
            🎦 Test Camera and Microphone
          </button>
          
          <div className="camera-preview">
            <video ref={localVideoRef} autoPlay muted playsInline />
          </div>
          
          <button 
            type="submit" 
            disabled={!isVerified || !devicesAvailable.camera || !devicesAvailable.microphone}
          >
            {!isVerified 
              ? "Verify Email to Stream" 
              : (!devicesAvailable.camera || !devicesAvailable.microphone)
                ? "Camera or Microphone Not Available"
                : "✅ Start Livestream"
            }
          </button>
        </form>
      ) : (
        <>
          <div className="stream-header">
            <h2>{title}</h2>
            <p className="host-info">You are hosting this stream</p>
          </div>
          <div className="stream-container">
            <div className="video-section">
              <div className="stream-info">
                <p>👀 Viewers: <strong>{viewerCount}</strong></p>
                <p>⏰ Time Left: <strong>{timeLeft}</strong></p>
              </div>
              <video ref={localVideoRef} autoPlay muted playsInline />
              <div className="stream-controls">
                {timeLeft === "⏰ Bidding ended" ? (
                  <button className="close-button" onClick={handleNavigateToAuctions}>
                    🏁 Return to Auctions
                  </button>
                ) : (
                  <button className="stop-button" onClick={stopCamera}>
                    🛑 End Stream
                  </button>
                )}
              </div>
            </div>

            <div className="chat-section">
              <div className="chat-header">
                <h3>💬 Live Chat</h3>
              </div>
              <div className="chat-messages">
                {messages.map((msg, i) => (
                  <div 
                    key={i} 
                    className={`chat-message ${/^\d+$/.test(msg.text) ? 'bid' : ''}`}
                  >
                    <strong>{msg.user}:</strong> {msg.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {isUploading && (
        <div className="upload-overlay">
          <div className="spinner"></div>
          <p>Starting livestream, please wait...</p>
        </div>
      )}
    </div>
  );
}

export default LiveStreamHost;
